import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Tune as TuneIcon, Info as InfoIcon } from '@mui/icons-material';
import { authService } from '../services/authService';
import { adInsightsService } from '../services/adInsightsService';
import { useNavigate } from 'react-router-dom';
import { tokens } from '../theme/tokens';

const AdPreferencesPage = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [labels, setLabels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const userData = await authService.getCurrentUser();
        const username = userData?.username || userData?.facebook_name || userData?.reddit_username;
        const userId = userData?.id;
        if (!username && !userId) { setError('Username not found. Please log in again.'); return; }
        const result = await adInsightsService.getAdInsights({ username, userId });
        if (result.status === 'success') {
          setRows(result.data?.fb_ad_preferences_rows || []);
          setLabels(result.data?.fb_ad_preferences || []);
        } else {
          setError(result.message || 'Failed to load ad preferences');
        }
      } catch (err) {
        console.error('Load ad preferences error:', err);
        if (err.response?.status === 401 || err.response?.status === 403) navigate('/login');
        else setError('Failed to load ad preferences data.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [navigate]);

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 6, textAlign: 'center' }}>
        <CircularProgress size={48} sx={{ color: 'primary.main' }} />
        <Typography variant="h6" sx={{ mt: 3, color: 'text.secondary' }}>Loading ad preferences...</Typography>
      </Container>
    );
  }

  const hasData = rows.length > 0 || labels.length > 0;

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 3, md: 4 }, pb: 6 }}>
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, sm: 3.5 },
          mb: 3,
          borderRadius: `${tokens.radius.card}px`,
          background: tokens.hero.gradient,
          border: tokens.hero.border,
          boxShadow: tokens.hero.shadow,
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1.5}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Box sx={{ p: 1.25, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.1)', display: 'flex', border: `1px solid ${tokens.border.subtle}` }}>
              <TuneIcon sx={{ color: tokens.text.primary, fontSize: 26 }} />
            </Box>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700, color: tokens.text.primary, letterSpacing: '-0.02em' }}>
                Ad Preferences
              </Typography>
              <Typography variant="body2" sx={{ color: tokens.text.secondary, mt: 0.25 }}>
                Topics and settings Meta uses to personalize your ads
              </Typography>
            </Box>
          </Stack>
          <Button
            variant="contained"
            size="small"
            onClick={() => navigate('/dashboard')}
            sx={{ bgcolor: tokens.accent.brandBlue, color: '#fff', fontWeight: 600, '&:hover': { bgcolor: '#1D4ED8' } }}
          >
            Back to Dashboard
          </Button>
        </Stack>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {!hasData && !error && (
        <Paper elevation={0} sx={{ p: 4, textAlign: 'center', borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
          <InfoIcon sx={{ fontSize: 54, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom sx={{ color: 'text.primary' }}>No ad preferences found</Typography>
          <Typography variant="body2" color="text.secondary">
            Upload your Facebook DYI ZIP on the Facebook page to see your ad preferences.
          </Typography>
        </Paper>
      )}

      {rows.length > 0 && (
        <Paper elevation={0} sx={{ mb: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ p: 2.5, borderBottom: 1, borderColor: 'divider' }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 700 }}>Ad preferences</Typography>
              <Chip label={rows.length} size="small" sx={{ bgcolor: 'rgba(24,184,154,0.15)', color: '#18B89A', fontWeight: 700 }} />
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Settings and topics from your export. Review in Facebook → Settings → Ads.
            </Typography>
          </Box>
          <Alert severity="info" sx={{ mx: 2, mt: 2 }} variant="outlined">
            Rows come from <code>ad_preferences.json</code>. Use Facebook → Settings → Ads for changes.
          </Alert>
          <TableContainer sx={{ maxHeight: 420 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>Setting / topic</TableCell>
                  <TableCell>Value / detail</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, idx) => (
                  <TableRow key={idx} hover>
                    <TableCell>{idx + 1}</TableCell>
                    <TableCell>{row.label}</TableCell>
                    <TableCell sx={{ wordBreak: 'break-word' }}>{row.value || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {rows.length === 0 && labels.length > 0 && (
        <Paper elevation={0} sx={{ mb: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ p: 2.5, borderBottom: 1, borderColor: 'divider' }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 700 }}>Ad preferences</Typography>
              <Chip label={labels.length} size="small" sx={{ bgcolor: 'rgba(24,184,154,0.15)', color: '#18B89A', fontWeight: 700 }} />
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Topics Meta uses to personalize ads. Review in Facebook → Settings → Ads.
            </Typography>
          </Box>
          <TableContainer sx={{ maxHeight: 420 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>Preference / topic</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {labels.map((row, idx) => (
                  <TableRow key={idx} hover>
                    <TableCell>{idx + 1}</TableCell>
                    <TableCell>{row}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Container>
  );
};

export default AdPreferencesPage;
